import React, { useState, useEffect } from 'react';
import { listFilesUploaded } from '../../../services';

/**
 * Componente para seleção de áudios por tenant usando a API existente de uploads
 * @param {Object} props - Props do componente
 * @param {string} props.selectedAudio - Áudio selecionado (alternativa: selectedFile)
 * @param {string} props.selectedFile - Arquivo selecionado (alternativa: selectedAudio)
 * @param {string} props.selectedType - Tipo de áudio selecionado
 * @param {function} props.onAudioChange - Callback quando áudio é alterado (formato objeto)
 * @param {function} props.onSelect - Callback quando áudio é alterado (formato parâmetros)
 * @param {string} props.tenant - Tenant para filtrar áudios (alternativa: accountCode)
 * @param {string} props.accountCode - AccountCode para filtrar áudios (alternativa: tenant)
 * @param {string} props.audioType - Tipo de áudio (ura, msg, moh) - opcional
 * @param {boolean} props.disabled - Se o dropdown está desabilitado
 */
const AudioSelectorUploads = ({ 
  selectedAudio = '', 
  selectedFile = '',
  selectedType = '',
  onAudioChange, 
  onSelect,
  tenant = null, 
  accountCode = null,
  audioType = null,
  disabled = false 
}) => {
  const [audios, setAudios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Normalizar props alternativas
  const effectiveTenant = tenant || accountCode;
  const effectiveSelectedAudio = selectedFile || selectedAudio;

  // Buscar áudios do tenant
  const fetchAudios = async () => {
    if (!effectiveTenant) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await listFilesUploaded();
      
      if (!result || result.error) {
        setError(result?.message || "Erro ao buscar lista de arquivos.");
        return;
      }

      const audioFiles = [];
      
      // Processar a estrutura de dados retornada pela API
      for (const [tenantKey, foldersObj] of Object.entries(result)) {
        // Filtrar apenas pelo tenant especificado
        if (tenantKey === effectiveTenant) {
          for (const [folderName, filesArr] of Object.entries(foldersObj)) {
            // Se audioType foi especificado, filtrar apenas por esse tipo
            if (!audioType || folderName === audioType) {
              if (Array.isArray(filesArr)) {
                filesArr.forEach((fileName) => {
                  // Verificar se é um arquivo de áudio válido
                  if (/\.(wav|mp3|gsm)$/i.test(fileName)) {
                    audioFiles.push({
                      file: fileName,
                      type: folderName,
                      accountcode: tenantKey,
                      size: 0, // A API atual não retorna tamanho
                      tenant: tenantKey,
                      folder: folderName
                    });
                  }
                });
              }
            }
          }
        }
      }

      // Ordenar por nome do arquivo
      audioFiles.sort((a, b) => a.file.localeCompare(b.file));
      setAudios(audioFiles);

    } catch (err) {
      console.error('Erro ao buscar áudios:', err);
      setError('Erro ao carregar lista de áudios');
    } finally {
      setLoading(false);
    }
  };

  // Buscar áudios quando tenant ou audioType mudar
  useEffect(() => {
    fetchAudios();
  }, [effectiveTenant, audioType]);

  // Lidar com mudança de seleção
  const handleChange = (event) => {
    const value = event.target.value;
    
    if (value === '') {
      // Chamar onAudioChange (formato objeto) se existir
      if (onAudioChange) {
        onAudioChange({
          file: '',
          type: '',
          accountcode: ''
        });
      }
      // Chamar onSelect (formato parâmetros) se existir
      if (onSelect) {
        onSelect('', '', '');
      }
    } else {
      const selectedAudioData = audios.find(audio => 
        `${audio.type}|${audio.accountcode}|${audio.file}` === value
      );
      
      if (selectedAudioData) {
        // Chamar onAudioChange (formato objeto) se existir
        if (onAudioChange) {
          onAudioChange({
            file: selectedAudioData.file,
            type: selectedAudioData.type,
            accountcode: selectedAudioData.accountcode
          });
        }
        // Chamar onSelect (formato parâmetros) se existir
        if (onSelect) {
          onSelect(selectedAudioData.file, selectedAudioData.type, selectedAudioData.accountcode);
        }
      }
    }
  };

  // Valor atual do select
  const currentValue = effectiveSelectedAudio && audios.length > 0 
    ? audios.find(audio => audio.file === effectiveSelectedAudio)
      ? `${audios.find(audio => audio.file === effectiveSelectedAudio).type}|${audios.find(audio => audio.file === effectiveSelectedAudio).accountcode}|${effectiveSelectedAudio}`
      : ''
    : '';

  return (
    <div className="field">
      <label className="label">Áudio para Reproduzir</label>
      <div className="control">
        <div className="select is-fullwidth">
          <select 
            value={currentValue}
            onChange={handleChange}
            disabled={disabled || loading || !effectiveTenant}
          >
            <option value="">Selecione um áudio (opcional)</option>
            
            {loading && (
              <option disabled>Carregando áudios...</option>
            )}
            
            {error && (
              <option disabled>Erro: {error}</option>
            )}
            
            {!loading && !error && audios.length === 0 && (
              <option disabled>Nenhum áudio encontrado</option>
            )}
            
            {!loading && !error && audios.map((audio, index) => (
              <option 
                key={index} 
                value={`${audio.type}|${audio.accountcode}|${audio.file}`}
              >
                [{audio.type.toUpperCase()}] {audio.file}
              </option>
            ))}
          </select>
        </div>
      </div>
      
      {selectedAudio && (
        <p className="help is-info">
          Áudio selecionado: <strong>{selectedAudio}</strong>
        </p>
      )}
      
      {error && (
        <p className="help is-danger">
          {error}
        </p>
      )}
      
      {!tenant && (
        <p className="help is-warning">
          Selecione um tenant para carregar os áudios disponíveis
        </p>
      )}
    </div>
  );
};

export default AudioSelectorUploads;
